/**
 * O botão de adicionar amigo — UM só, para todos os sítios onde aparece o nome
 * de uma pessoa: o cartão de perfil (chat, lista de membros, escritório 2D) e a
 * lista de quem está no escritório.
 *
 * Um botão destes é feito de uma regra, não de um clique: quando é que ele
 * aparece, e o que é que ele faz quando já existe uma relação a meio. Escrever
 * essa regra à mão em cada sítio é exactamente como o botão do microfone acabou
 * com três variantes que já discordavam entre si — a do rodapé nem sequer
 * olhava para a permissão de falar. Por isso a regra vive UMA vez, em
 * `friendStep`, e o resto do ficheiro só a desenha.
 *
 * A regra:
 *   eu próprio          → nada (não me adiciono)
 *   sem relação         → "Adicionar amigo"
 *   pedido recebido     → "Aceitar pedido"  (aceitar é melhor que enviar outro)
 *   pedido enviado      → "Pedido enviado", inerte (o cancelar vive na lista de
 *                          pendentes, que é onde se vai quando se quer desfazer)
 *   já amigo            → nada — foi literalmente o pedido: «caso eu nao tenha vc»
 *   bloqueado / bloqueou→ nada (um pedido daqui só ia falhar no servidor)
 */

import type { PublicUser, RelationshipKind } from "@greenvoice/shared";
import { useState } from "react";

import { ApiError, api } from "../lib/api";
import { useStore } from "../store/store";
import { IconUserPlus } from "./Icons";

/** O que o botão tem para oferecer a esta pessoa, ou nada. */
export type FriendStep = "add" | "accept" | "sent";

/**
 * A regra, isolada de React de propósito: é a parte que se pode partir sem dar
 * por ela e a única que vale a pena testar sozinha.
 */
export function friendStep(kind: RelationshipKind | undefined, isSelf: boolean): FriendStep | null {
  if (isSelf) return null;
  switch (kind) {
    case undefined:
      return "add";
    case "INCOMING_REQUEST":
      return "accept";
    case "OUTGOING_REQUEST":
      return "sent";
    // FRIEND, BLOCKED e BLOCKED_BY não têm botão nenhum a fazer aqui.
    default:
      return null;
  }
}

const LABEL: Record<FriendStep, string> = {
  add: "Adicionar amigo",
  accept: "Aceitar pedido",
  sent: "Pedido enviado",
};

interface AddFriendButtonProps {
  user: PublicUser;
  /**
   * O botão veste-se do sítio onde está: `user-card__action` no cartão (coluna,
   * ícone à esquerda), `button button--small` numa linha de lista. É a única
   * diferença entre os dois usos — se um dia forem precisas duas diferenças, é
   * sinal de que são dois botões e não um.
   */
  className?: string;
}

export function AddFriendButton({ user, className = "button button--small button--primary" }: AddFriendButtonProps) {
  const selfId = useStore((s) => s.self?.id ?? null);
  const kind = useStore((s) => s.relationships[user.id]?.kind);
  const applyEvent = useStore((s) => s.applyEvent);

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const step = friendStep(kind, selfId === user.id);

  /**
   * Depois de correr bem, o botão desaparecia sem dizer nada — o `kind` passa a
   * FRIEND ou OUTGOING_REQUEST e a regra lá em cima devolve `null`/inerte. Um
   * botão que some é indistinguível de um botão que falhou, e o cartão fecha-se
   * logo a seguir, portanto a confirmação tem de estar AQUI e não noutro ecrã.
   */
  if (done) {
    return (
      <span className="friend-add__note" role="status">
        {done}
      </span>
    );
  }
  if (!step) return null;

  async function run() {
    if (step === "sent") return;
    setBusy(true);
    setError(null);
    try {
      // Adicionar é por NOME DE UTILIZADOR (é o que a API aceita); aceitar é por
      // id. A diferença é da API, não da UI — daqui é o mesmo botão.
      const relation =
        step === "accept" ? await api.users.acceptFriend(user.id) : await api.users.requestFriend(user.username);
      // Não se espera pelo eco do gateway: o `applyEvent` é idempotente por id,
      // e o clique tem de responder na hora mesmo com a rede lenta.
      applyEvent({ type: "RelationshipUpdate", user: relation.user, kind: relation.kind });
      setDone(relation.kind === "FRIEND" ? "Agora são amigos" : "Pedido enviado");
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 404
          ? "Não há ninguém com esse nome de usuário."
          : (caught as Error).message,
      );
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className={className} disabled={busy || step === "sent"} onClick={() => void run()}>
        <IconUserPlus size={16} />
        {busy ? "Enviando…" : LABEL[step]}
      </button>
      {/* O erro fica ao pé do botão que o provocou, e não num `title`: um
          tooltip não existe no telemóvel e some assim que o dedo sai. */}
      {error ? (
        <span className="friend-add__error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}
